import { useEffect } from "react";
import { CheckCircle2, ExternalLink, Mail } from "lucide-react";
import { LINKS } from "@/lib/constants";

const STEPS = [
  { n: "01", t: "Open TradingView" },
  { n: "02", t: "Click Indicators Menu" },
  { n: "03", t: "Open Invite-Only Scripts Folder" },
];

const SuccessHub = () => {
  useEffect(() => {
    document.title = "Access Pending — SHYFT ORB Injection";
  }, []);

  return (
    <main
      data-testid="success-hub-page"
      className="relative min-h-screen flex items-center justify-center px-6 py-24"
    >
      <div
        aria-hidden
        className="vault-spotlight"
        style={{ top: "5%", left: "50%", transform: "translateX(-50%)", width: 800, height: 800 }}
      />

      <div className="relative w-full max-w-3xl">
        <a
          href="/"
          className="eyebrow inline-flex items-center gap-2 text-[#b8b3aa] hover:text-white"
          data-testid="success-back"
        >
          ← Back to vault
        </a>

        <div className="mt-6 rounded-[2.5rem] border border-[rgba(201,162,91,0.3)] bg-[#14110e]/85 backdrop-blur p-8 md:p-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(201,162,91,0.4)] bg-[#0a0a0a]/60">
            <CheckCircle2 size={14} className="text-emerald-400" />
            <span className="eyebrow !text-[10px] !text-[#e7c98a]">
              Purchase Confirmed
            </span>
          </div>

          <h1 className="font-serif-display mt-6 text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
            <span className="italic text-gradient-gold">Access Pending.</span>
          </h1>
          <p className="mt-5 text-[#b8b3aa] text-base md:text-lg leading-relaxed max-w-2xl">
            Our system is injecting the <strong className="text-white">SHYFT ORB</strong> script into your
            TradingView profile within <strong className="text-white">24 hours</strong>. Once injected, follow
            the three steps below to deploy the indicator on any chart.
          </p>

          {/* Steps pill */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div
                key={s.n}
                data-testid={`success-step-${s.n}`}
                className="rounded-full border border-[rgba(201,162,91,0.35)] bg-[#0a0a0a]/60 px-5 py-4 flex items-center gap-4"
              >
                <span className="font-serif-display text-2xl text-gold shrink-0">
                  {s.n}
                </span>
                <span className="text-white text-sm leading-tight">{s.t}</span>
              </div>
            ))}
          </div>

          {/* Visual flow */}
          <div className="mt-10 rounded-[2rem] border border-[rgba(201,162,91,0.18)] bg-[#0a0a0a]/60 p-6 md:p-8">
            <div className="eyebrow">What happens next</div>
            <ul className="mt-5 space-y-4 text-sm text-[#cfc8b9] leading-relaxed">
              <li className="flex gap-3">
                <span className="text-gold">·</span>
                You&rsquo;ll receive a confirmation email with your order details
                and the TradingView username you submitted at checkout.
              </li>
              <li className="flex gap-3">
                <span className="text-gold">·</span>
                Our team pushes the invite-only script to your TradingView
                account within 24 hours.
              </li>
              <li className="flex gap-3">
                <span className="text-gold">·</span>
                Your license is valid for{" "}
                <strong className="text-white">365 days</strong> from today.
                It does not auto-renew.
              </li>
            </ul>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="https://www.tradingview.com/chart/"
              target="_blank"
              rel="noreferrer"
              data-testid="open-tradingview"
              className="btn-gold"
            >
              Open TradingView <ExternalLink size={16} />
            </a>
            <a
              href={`mailto:${LINKS.supportEmail}`}
              data-testid="contact-support"
              className="btn-outline-gold"
            >
              <Mail size={16} />
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SuccessHub;
